import {
  Box,
  Button,
  FormGroup,
  FormHelperText,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React from 'react'
import { Loader, Plus, Trash } from 'react-feather'
import { Control, FieldError, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { HourSeriesCollectionFragment } from '../../generated/graphql'
import { validators } from '../../validation/validators'
import { ControlledTextField } from './ControlledTextField'
import type { CreatePartFormValue } from '../building/basic-info-dialog/form-values'

const useStyles = makeStyles((theme) => ({
  dropdown: {
    width: '60%',
    marginRight: theme.spacing(2),
  },
}))

type CreateProps = {
  control: Control
  name: string
  error?: FieldError
  templates?: HourSeriesCollectionFragment[]
  defaultTemplate?: HourSeriesCollectionFragment
}

export function ControlledCreatePartsEditor(
  props: CreateProps,
): React.ReactElement {
  const { control, name, error, templates, defaultTemplate } = props

  const {
    fields: parts,
    append,
    remove,
  } = useFieldArray<CreatePartFormValue, 'key'>({
    keyName: 'key',
    control,
    name,
  })

  const classes = useStyles()
  const { t } = useTranslation()

  const addPart = () => {
    append({
      // Give each new part a unique id.
      // This is not used in the backend but required by useFieldArray
      id: uuid(),
      templateId: defaultTemplate?.id ?? '',
      area: '0',
    })
  }

  const templateOptions = templates ? (
    templates.map((template) => (
      <MenuItem key={template.id} value={template.id}>
        {template.name}
      </MenuItem>
    ))
  ) : (
    <Loader />
  )

  return (
    <Box id={`part-editor-${name}`}>
      {parts.map((part, index) => (
        <Box key={part.key} display="flex" flexDirection="row" py={2}>
          <Box display="none">
            <ControlledTextField
              control={control}
              name={`${name}.${index}.id`}
              defaultValue={part.id}
            />
          </Box>

          <FormGroup className={classes.dropdown}>
            <ControlledTextField
              control={control}
              name={`${name}.${index}.templateId`}
              defaultValue={part.templateId}
              rules={validators.required}
              type="text"
              label={t('createBuilding.pages.parts.partsDropdownLabel')}
              select
            >
              {templateOptions}
            </ControlledTextField>
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name={`${name}.${index}.area`}
              defaultValue={part.area}
              rules={validators.required}
              type="number"
              label={t('createBuilding.pages.parts.partArea')}
            />
          </FormGroup>

          <Button onClick={() => remove(index)}>
            <Trash />
          </Button>
        </Box>
      ))}

      {error && <FormHelperText error>{error}</FormHelperText>}

      <Button onClick={addPart}>
        <Plus />
        <Typography variant="body1">
          {t('createBuilding.pages.parts.addPart')}
        </Typography>
      </Button>
    </Box>
  )
}
